import { useEffect, useRef, useState } from 'react';

/*
 * Condense when the user scrolls down past CONDENSE_OFFSET; expand again as soon as
 * they scroll up (any amount), including while still mid-page (CPO-4599 follow-up).
 * Scroll is handled with a single capture listener on the main column so nested
 * MFE/loginless scrollers are covered without per-node bookkeeping.
 */
const CONDENSE_OFFSET = 48;
/** Ignore expand triggers from elastic overscroll near the bottom of a scroller. */
const BOTTOM_BOUNCE_ZONE = 48;
const SCROLL_EDGE_SLACK = 2;

const isScrollable = (element: HTMLElement): boolean => {
    const { overflowY } = window.getComputedStyle(element);
    if (overflowY !== 'auto' && overflowY !== 'scroll') {
        return false;
    }
    return element.scrollHeight > element.clientHeight + 1;
};

const isEventInColumn = (root: HTMLElement, event: Event): boolean => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(root)) {
        return true;
    }
    const target = event.target;
    return target instanceof Node && root.contains(target);
};

const maxScrollTopInColumn = (root: HTMLElement): number => {
    let max = root.scrollTop;
    root.querySelectorAll<HTMLElement>('*').forEach(node => {
        if (isScrollable(node)) {
            max = Math.max(max, node.scrollTop);
        }
    });
    return max;
};

const getMaxScrollTop = (element: HTMLElement): number =>
    Math.max(0, element.scrollHeight - element.clientHeight);

const isAtScrollBottom = (element: HTMLElement): boolean => {
    const max = getMaxScrollTop(element);
    return max <= 0 || element.scrollTop >= max - SCROLL_EDGE_SLACK;
};

const forEachScrollableInColumn = (root: HTMLElement, visit: (element: HTMLElement) => void) => {
    if (root.scrollHeight > root.clientHeight + 1) {
        visit(root);
    }
    root.querySelectorAll<HTMLElement>('*').forEach(node => {
        if (isScrollable(node)) {
            visit(node);
        }
    });
};

/** True when every overflow scroller in the column is pinned to its bottom edge. */
const columnAtScrollBottom = (root: HTMLElement): boolean => {
    let foundScrollable = false;
    let allAtBottom = true;
    forEachScrollableInColumn(root, element => {
        foundScrollable = true;
        if (!isAtScrollBottom(element)) {
            allAtBottom = false;
        }
    });
    return foundScrollable && allAtBottom;
};

const updateBottomEngagedLatch = (
    element: HTMLElement,
    previous: number,
    scrollTop: number,
    peakScrollTop: number,
    bottomEngaged: Map<HTMLElement, boolean>
) => {
    const max = getMaxScrollTop(element);
    if (max <= 0) {
        bottomEngaged.delete(element);
        return;
    }
    const atBottomEdge = (offset: number) => offset >= max - SCROLL_EDGE_SLACK;
    if (atBottomEdge(scrollTop) || atBottomEdge(previous) || atBottomEdge(peakScrollTop)) {
        bottomEngaged.set(element, true);
    }
    if (scrollTop < max - BOTTOM_BOUNCE_ZONE) {
        bottomEngaged.set(element, false);
    }
};

const shouldIgnoreExpandFromRubberBand = (
    element: HTMLElement,
    scrollTop: number,
    bottomEngaged: Map<HTMLElement, boolean>
): boolean => {
    const max = getMaxScrollTop(element);
    if (max <= 0) {
        return false;
    }
    return bottomEngaged.get(element) === true && scrollTop >= max - BOTTOM_BOUNCE_ZONE;
};

const columnHasScrollableOverflow = (root: HTMLElement): boolean => {
    if (root.scrollHeight > root.clientHeight + 1) {
        return true;
    }
    return [...root.querySelectorAll<HTMLElement>('*')].some(isScrollable);
};

const seedScrollPositions = (root: HTMLElement, scrollTopByTarget: Map<HTMLElement, number>) => {
    scrollTopByTarget.set(root, root.scrollTop);
    root.querySelectorAll<HTMLElement>('*').forEach(node => {
        if (isScrollable(node)) {
            scrollTopByTarget.set(node, node.scrollTop);
        }
    });
};

export const useScrollDirection = (scrollRoot: HTMLElement | null): { condensed: boolean } => {
    const [condensed, setCondensed] = useState(false);
    const animationFrame = useRef<number | null>(null);
    const lastScrollTopByTarget = useRef<Map<HTMLElement, number>>(new Map());

    useEffect(() => {
        const root = scrollRoot;
        if (!root) {
            return;
        }

        let cancelled = false;
        let wheelCondenseRaf: number | null = null;
        let wheelIntent: 'down' | 'up' | null = null;
        const scrollTopByTarget = lastScrollTopByTarget.current;
        const bottomEngagedByTarget = new Map<HTMLElement, boolean>();
        const pendingPeakScrollTopByTarget = new Map<HTMLElement, number>();
        const pendingScrollTargets = new Set<HTMLElement>();

        const cancelWheelCondenseRaf = () => {
            if (wheelCondenseRaf !== null) {
                window.cancelAnimationFrame(wheelCondenseRaf);
                wheelCondenseRaf = null;
            }
        };

        const applyScrollDelta = (
            element: HTMLElement,
            scrollTop: number,
            peakScrollTop: number
        ) => {
            const previous = scrollTopByTarget.get(element);
            scrollTopByTarget.set(element, scrollTop);

            if (previous === undefined) {
                if (scrollTop > CONDENSE_OFFSET) {
                    setCondensed(true);
                }
                return;
            }

            updateBottomEngagedLatch(
                element,
                previous,
                scrollTop,
                peakScrollTop,
                bottomEngagedByTarget
            );

            if (scrollTop < previous) {
                if (shouldIgnoreExpandFromRubberBand(element, scrollTop, bottomEngagedByTarget)) {
                    return;
                }
                setCondensed(false);
                return;
            }
            if (scrollTop > previous && scrollTop > CONDENSE_OFFSET) {
                setCondensed(true);
            }
        };

        const scheduleScrollDelta = (element: HTMLElement) => {
            const sample = element.scrollTop;
            const pendingPeak = pendingPeakScrollTopByTarget.get(element);
            pendingPeakScrollTopByTarget.set(
                element,
                pendingPeak === undefined ? sample : Math.max(pendingPeak, sample)
            );
            pendingScrollTargets.add(element);

            if (animationFrame.current !== null) {
                return;
            }
            animationFrame.current = window.requestAnimationFrame(() => {
                animationFrame.current = null;
                if (cancelled) {
                    return;
                }
                const targets = [...pendingScrollTargets];
                pendingScrollTargets.clear();
                for (const target of targets) {
                    const peakScrollTop =
                        pendingPeakScrollTopByTarget.get(target) ?? target.scrollTop;
                    pendingPeakScrollTopByTarget.delete(target);
                    applyScrollDelta(target, target.scrollTop, peakScrollTop);
                }
            });
        };

        const handleScrollCapture = (event: Event) => {
            if (!isEventInColumn(root, event)) {
                return;
            }
            const target = event.target;
            if (target instanceof HTMLElement) {
                scheduleScrollDelta(target);
            }
        };

        const handleWheel = (event: WheelEvent) => {
            if (!isEventInColumn(root, event)) {
                return;
            }
            if (event.deltaY > 0) {
                wheelIntent = 'down';
                const condenseIfPastThreshold = () => {
                    if (wheelIntent !== 'down') {
                        return;
                    }
                    const maxScrollTop = maxScrollTopInColumn(root);
                    if (maxScrollTop > CONDENSE_OFFSET || !columnHasScrollableOverflow(root)) {
                        setCondensed(true);
                    }
                };
                cancelWheelCondenseRaf();
                condenseIfPastThreshold();
                wheelCondenseRaf = window.requestAnimationFrame(() => {
                    wheelCondenseRaf = null;
                    if (!cancelled) {
                        condenseIfPastThreshold();
                    }
                });
                return;
            }
            if (event.deltaY < 0) {
                wheelIntent = 'up';
                cancelWheelCondenseRaf();
                if (columnAtScrollBottom(root)) {
                    return;
                }
                setCondensed(false);
            }
        };

        const handleRootScroll = () => {
            scheduleScrollDelta(root);
        };

        seedScrollPositions(root, scrollTopByTarget);
        setCondensed(maxScrollTopInColumn(root) > CONDENSE_OFFSET);

        /*
         * Bubble listener covers the main column when it is the scroll target (including jsdom
         * tests). Capture covers nested overflow scrollers inside the column.
         */
        root.addEventListener('scroll', handleRootScroll, { passive: true });
        root.addEventListener('scroll', handleScrollCapture, { capture: true, passive: true });
        document.addEventListener('wheel', handleWheel, { capture: true, passive: true });

        return () => {
            cancelled = true;
            cancelWheelCondenseRaf();
            root.removeEventListener('scroll', handleRootScroll);
            root.removeEventListener('scroll', handleScrollCapture, true);
            document.removeEventListener('wheel', handleWheel, true);
            scrollTopByTarget.clear();
            bottomEngagedByTarget.clear();
            pendingPeakScrollTopByTarget.clear();
            pendingScrollTargets.clear();
            if (animationFrame.current !== null) {
                window.cancelAnimationFrame(animationFrame.current);
                animationFrame.current = null;
            }
        };
    }, [scrollRoot]);

    return { condensed };
};
